import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, errMsg, refLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function Field({ f, value, onChange, refs, entity }) {
  const id = `${entity}-field-${f.name}`;
  if (f.type === "select" || f.type?.startsWith("ref:")) {
    const opts = f.type === "select" ? f.options.map((o) => ({ v: o, l: o })) : (refs[f.type.slice(4)] || []).map((r) => ({ v: r.id, l: refLabel(r) }));
    return (
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger data-testid={id}><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
        <SelectContent className="max-h-72">{opts.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  if (f.type === "textarea") return <Textarea data-testid={id} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} />;
  const type = { number: "number", date: "date", datetime: "datetime-local" }[f.type] || "text";
  return <Input data-testid={id} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

export default function EntityForm({ entity, open, onClose, initial, defaults, refs, onSaved }) {
  const { schema } = useAuth();
  const cfg = schema?.entities[entity];
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setData(initial || defaults || {}); }, [open, initial, defaults]);
  if (!cfg) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = initial ? await api.put(`/${entity}/${initial.id}`, data) : await api.post(`/${entity}`, data);
      toast.success(`${cfg.label.replace(/s$/, "")} ${initial ? "updated" : "created"}`);
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-slate-800" data-testid={`${entity}-form-dialog`}>
        <DialogHeader><DialogTitle>{initial ? "Edit" : "New"} {cfg.label.replace(/s$/, "")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {cfg.fields.map((f) => (
            <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
              <Label className="text-xs text-slate-400">{f.label}{f.required && <span className="text-rose-400"> *</span>}</Label>
              <Field f={f} entity={entity} refs={refs} value={data[f.name]} onChange={(v) => setData((d) => ({ ...d, [f.name]: v }))} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} data-testid={`${entity}-form-cancel`}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid={`${entity}-form-submit`}>{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
